import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  writeBatch,
  serverTimestamp,
  updateDoc
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { asInt, toDate } from '../utils/format';

export async function getSubscriptionAccess(tenantId) {
  if (!tenantId || tenantId === 'super_admin') {
    return {
      isActive: tenantId === 'super_admin',
      status: tenantId === 'super_admin' ? 'active' : 'inactive',
      maxScreens: tenantId === 'super_admin' ? 999 : 0,
      usedScreens: 0,
      hasScreenSlot: tenantId === 'super_admin',
      message: tenantId === 'super_admin' ? 'Platform access active.' : 'No tenant assigned.'
    };
  }

  const [subscriptionsSnap, tenantSnap, screensSnap] = await Promise.all([
    getDocs(query(collection(db, 'subscriptions'), where('tenantId', '==', tenantId))),
    getDoc(doc(db, 'tenants', tenantId)),
    getDocs(query(collection(db, 'screens'), where('tenantId', '==', tenantId)))
  ]);

  const now = new Date();
  const activeSubs = subscriptionsSnap.docs
    .map(item => ({ id: item.id, ...item.data() }))
    .filter(item => String(item.status || '').toLowerCase() === 'active')
    .sort((a, b) => (toDate(b.endDate)?.getTime() || 0) - (toDate(a.endDate)?.getTime() || 0));

  const selected = activeSubs[0] || null;
  const tenant = tenantSnap.exists() ? tenantSnap.data() : {};
  const usedScreens = screensSnap.docs.filter(item => !['deleted', 'removed'].includes(String(item.data().status || '').toLowerCase())).length;

  if (['suspended', 'disabled', 'deleted'].includes(String(tenant.status || '').toLowerCase())) {
    return { isActive: false, status: 'suspended', maxScreens: 0, usedScreens, hasScreenSlot: false, message: 'Account suspended. Contact the platform administrator.' };
  }

  if (!selected) {
    return { isActive: false, status: 'inactive', maxScreens: 0, usedScreens, hasScreenSlot: false, message: 'No active subscription. Purchase a package before using signage modules.' };
  }

  let maxScreens = asInt(selected.screenLimit ?? selected.maxScreens);
  let maxStorageGB = asInt(selected.storageLimitGB ?? selected.maxStorageGB);
  if ((maxScreens <= 0 || maxStorageGB <= 0) && selected.planId) {
    const planSnap = await getDoc(doc(db, 'plans', selected.planId));
    if (planSnap.exists()) {
      const plan = planSnap.data();
      if (maxScreens <= 0) maxScreens = asInt(plan.screens ?? plan.maxScreens);
      if (maxStorageGB <= 0) maxStorageGB = asInt(plan.storage ?? plan.maxStorageGB);
    }
  }
  if (maxScreens <= 0) maxScreens = asInt(tenant.maxScreens);
  if (maxStorageGB <= 0) maxStorageGB = asInt(tenant.maxStorageGB);

  const endDate = toDate(selected.endDate);
  if (endDate && endDate <= now) {
    return {
      isActive: false,
      isExpired: true,
      status: 'expired',
      subscriptionId: selected.id,
      planId: selected.planId,
      planName: selected.planName,
      endDate,
      maxScreens,
      maxStorageGB,
      usedScreens,
      hasScreenSlot: false,
      message: 'Subscription expired. Renew to resume content management and TV playback.'
    };
  }

  const hasScreenSlot = maxScreens >= 999 || usedScreens < maxScreens;
  return {
    isActive: true,
    isExpired: false,
    status: 'active',
    subscriptionId: selected.id,
    planId: selected.planId,
    planName: selected.planName,
    startDate: toDate(selected.startDate),
    endDate,
    maxScreens,
    maxStorageGB,
    usedScreens,
    hasScreenSlot,
    message: hasScreenSlot ? 'Subscription active.' : 'Screen limit reached for the current package.'
  };
}

export async function createPurchaseRequest({ tenantId, plan }) {
  const existing = await getDocs(query(collection(db, 'subscription_requests'), where('tenantId', '==', tenantId), where('status', '==', 'pending')));
  const batch = writeBatch(db);
  existing.forEach(item => batch.update(item.ref, { status: 'superseded', updatedAt: serverTimestamp() }));
  const requestRef = doc(collection(db, 'subscription_requests'));
  batch.set(requestRef, {
    tenantId,
    planId: plan.id,
    planName: plan.name,
    amount: Number(plan.price || 0),
    billingPeriod: plan.billingCycle || 'month',
    status: 'pending',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  await batch.commit();
  return requestRef.id;
}

export async function activateSubscription({ tenantId, plan, requestId = null, paymentReference = '', activatedBy = '' }) {
  if (!tenantId || !plan?.id) throw new Error('Tenant and plan are required.');

  const [tenantSnap, existingSubscriptions] = await Promise.all([
    getDoc(doc(db, 'tenants', tenantId)),
    getDocs(query(collection(db, 'subscriptions'), where('tenantId', '==', tenantId)))
  ]);
  if (!tenantSnap.exists()) throw new Error('Company not found.');

  const now = new Date();
  const durationDays = Math.max(1, asInt(plan.durationDays) || 30);
  const endDate = new Date(now.getTime() + durationDays * 86_400_000);
  const screenLimit = asInt(plan.screens ?? plan.maxScreens);
  const storageLimitGB = asInt(plan.storage ?? plan.maxStorageGB);
  const subscriptionRef = doc(collection(db, 'subscriptions'));

  const batch = writeBatch(db);
  existingSubscriptions.docs.forEach(item => {
    if (String(item.data().status || '').toLowerCase() === 'active') {
      batch.update(item.ref, { status: 'replaced', replacedAt: serverTimestamp(), updatedAt: serverTimestamp() });
    }
  });

  batch.set(subscriptionRef, {
    tenantId,
    planId: plan.id,
    planName: plan.name,
    amount: Number(plan.price || 0),
    billingCycle: plan.billingCycle || 'month',
    durationDays,
    screenLimit,
    storageLimitGB,
    startDate: now,
    endDate,
    status: 'active',
    paymentReference: paymentReference || '',
    activatedBy: activatedBy || '',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });

  batch.update(doc(db, 'tenants', tenantId), {
    status: 'active',
    subscriptionStatus: 'active',
    currentPlanId: plan.id,
    currentPlanName: plan.name,
    subscriptionId: subscriptionRef.id,
    subscriptionStartDate: now,
    subscriptionExpiry: endDate,
    maxScreens: screenLimit,
    maxStorageGB: storageLimitGB,
    updatedAt: serverTimestamp()
  });

  if (requestId) {
    batch.update(doc(db, 'subscription_requests', requestId), {
      status: 'approved',
      approvedAt: serverTimestamp(),
      subscriptionId: subscriptionRef.id,
      paymentReference: paymentReference || '',
      updatedAt: serverTimestamp()
    });
  }

  if (Number(plan.price || 0) > 0 || paymentReference) {
    const paymentRef = doc(collection(db, 'payments'));
    batch.set(paymentRef, {
      tenantId,
      subscriptionId: subscriptionRef.id,
      planId: plan.id,
      amount: Number(plan.price || 0),
      paymentReference: paymentReference || '',
      status: 'verified',
      createdAt: serverTimestamp()
    });
  }

  await batch.commit();
  await syncTenantScreenEntitlements(tenantId, screenLimit, true);
  return subscriptionRef.id;
}

export async function renewSubscription({ subscriptionId, tenantId, plan, paymentReference = '', activatedBy = '' }) {
  if (!subscriptionId) return activateSubscription({ tenantId, plan, paymentReference, activatedBy });
  const subSnap = await getDoc(doc(db, 'subscriptions', subscriptionId));
  if (!subSnap.exists()) return activateSubscription({ tenantId, plan, paymentReference, activatedBy });

  const current = subSnap.data();
  const now = new Date();
  const previousEnd = toDate(current.endDate);
  const base = previousEnd && previousEnd > now ? previousEnd : now;
  const durationDays = Math.max(1, asInt(plan.durationDays) || 30);
  const endDate = new Date(base.getTime() + durationDays * 86_400_000);
  const screenLimit = asInt(plan.screens ?? plan.maxScreens);
  const storageLimitGB = asInt(plan.storage ?? plan.maxStorageGB);

  const batch = writeBatch(db);
  batch.update(doc(db, 'subscriptions', subscriptionId), {
    planId: plan.id,
    planName: plan.name,
    amount: Number(plan.price || 0),
    billingCycle: plan.billingCycle || 'month',
    durationDays,
    screenLimit,
    storageLimitGB,
    endDate,
    status: 'active',
    paymentReference: paymentReference || current.paymentReference || '',
    renewedAt: serverTimestamp(),
    activatedBy: activatedBy || current.activatedBy || '',
    updatedAt: serverTimestamp()
  });
  batch.update(doc(db, 'tenants', tenantId), {
    status: 'active',
    subscriptionStatus: 'active',
    currentPlanId: plan.id,
    currentPlanName: plan.name,
    subscriptionId,
    subscriptionExpiry: endDate,
    maxScreens: screenLimit,
    maxStorageGB: storageLimitGB,
    updatedAt: serverTimestamp()
  });
  const paymentRef = doc(collection(db, 'payments'));
  batch.set(paymentRef, {
    tenantId,
    subscriptionId,
    planId: plan.id,
    amount: Number(plan.price || 0),
    paymentReference: paymentReference || '',
    status: 'verified',
    type: 'renewal',
    createdAt: serverTimestamp()
  });
  await batch.commit();
  await syncTenantScreenEntitlements(tenantId, screenLimit, true);
}

export async function cancelSubscription({ subscriptionId, tenantId, reason = 'Cancelled by Super Admin' }) {
  const batch = writeBatch(db);
  if (subscriptionId) {
    batch.update(doc(db, 'subscriptions', subscriptionId), {
      status: 'cancelled',
      cancelReason: reason,
      cancelledAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  }
  batch.update(doc(db, 'tenants', tenantId), {
    subscriptionStatus: 'cancelled',
    maxScreens: 0,
    maxStorageGB: 0,
    updatedAt: serverTimestamp()
  });
  await batch.commit();
  await syncTenantScreenEntitlements(tenantId, 0, false);
}

export async function syncTenantScreenEntitlements(tenantId, screenLimit, subscriptionActive = true) {
  const snapshot = await getDocs(query(collection(db, 'screens'), where('tenantId', '==', tenantId)));
  const screens = snapshot.docs
    .filter(item => !['deleted', 'removed'].includes(String(item.data().status || '').toLowerCase()))
    .sort((a, b) => {
      const aDate = toDate(a.data().pairedAt || a.data().createdAt)?.getTime() || 0;
      const bDate = toDate(b.data().pairedAt || b.data().createdAt)?.getTime() || 0;
      return aDate - bDate;
    });

  const limit = asInt(screenLimit);
  for (let offset = 0; offset < screens.length; offset += 400) {
    const batch = writeBatch(db);
    screens.slice(offset, offset + 400).forEach((screen, localIndex) => {
      const index = offset + localIndex;
      const overLimit = !subscriptionActive || limit <= 0 || (limit < 999 && index >= limit);
      batch.update(screen.ref, {
        subscriptionBlocked: overLimit,
        subscriptionBlockReason: !subscriptionActive ? 'subscription_inactive' : overLimit ? 'screen_limit_exceeded' : null,
        subscriptionUpdatedAt: serverTimestamp()
      });
    });
    await batch.commit();
  }
}

export async function suspendTenant(tenantId, suspended = true) {
  await updateDoc(doc(db, 'tenants', tenantId), {
    status: suspended ? 'suspended' : 'active',
    updatedAt: serverTimestamp()
  });
  const access = suspended ? null : await getSubscriptionAccess(tenantId);
  await syncTenantScreenEntitlements(tenantId, suspended ? 0 : (access?.maxScreens || 0), !suspended && Boolean(access?.isActive));
}
